import React from "react";
import {
  MDBModal,
  MDBModalDialog,
  MDBModalContent,
  MDBModalHeader,
  MDBModalTitle,
  MDBModalBody,
  MDBModalFooter,
  MDBBtn,
  MDBIcon,
  MDBRow,
  MDBCol,
  MDBInput,
  MDBBadge,
  MDBListGroup,
  MDBListGroupItem,
  MDBSpinner,
} from "mdb-react-ui-kit";

import { doFetchJson } from "Projects/CC_cubi_clan_project/Utils/ConnectionManager";
import { toast } from "Projects/CC_cubi_clan_project/GlobalComponents/ToastHost/ToastHost";

/**
 * SellTicketModal — vender una entrada (estado AVAILABLE → SOLD).
 *
 * Props:
 *  - show: boolean
 *  - ticket: TicketDTO
 *  - batchPrice: número opcional (precio por defecto)
 *  - onClose: () => void
 *  - onSold: (ticket) => void
 */
class SellTicketModal extends React.Component {
  constructor(props) {
    super(props);
    this.state = this.initialState(props);
    this.searchTimer = null;
  }

  componentDidUpdate(prevProps) {
    if (prevProps.show !== this.props.show && this.props.show) {
      this.setState(this.initialState(this.props));
    }
  }

  initialState = (props) => ({
    customerQuery: "",
    customers: [],
    searching: false,
    selectedCustomer: null,
    soldToName: "",
    soldToEmail: "",
    soldToPhone: "",
    soldToNotes: "",
    pricePaid: props.batchPrice != null ? String(props.batchPrice) : "",
    paymentMethod: "CASH",
    saleLocation: "",
    saving: false,
  });

  handleClose = () => {
    if (this.state.saving) return;
    if (this.searchTimer) clearTimeout(this.searchTimer);
    if (this.props.onClose) this.props.onClose();
  };

  handleQueryChange = (e) => {
    const v = e.target.value;
    this.setState({ customerQuery: v, selectedCustomer: null });
    if (this.searchTimer) clearTimeout(this.searchTimer);
    if (v.trim().length < 2) {
      this.setState({ customers: [] });
      return;
    }
    this.searchTimer = setTimeout(() => this.runCustomerSearch(v.trim()), 300);
  };

  runCustomerSearch = (q) => {
    this.setState({ searching: true });
    doFetchJson("TEAN", "getCustomers?q=" + encodeURIComponent(q) + "&limit=10")
      .then((json) => {
        if (json && json.STATE === "SUCCESS") {
          this.setState({ customers: json.DATA || [] });
        } else {
          this.setState({ customers: [] });
        }
      })
      .catch(() => this.setState({ customers: [] }))
      .finally(() => this.setState({ searching: false }));
  };

  selectCustomer = (c) => {
    this.setState({
      selectedCustomer: c,
      soldToName: c.name || "",
      soldToEmail: c.email || "",
      soldToPhone: c.phone || "",
      customers: [],
      customerQuery: c.name || "",
    });
  };

  clearCustomer = () => {
    this.setState({
      selectedCustomer: null,
      customerQuery: "",
      customers: [],
      soldToName: "",
      soldToEmail: "",
      soldToPhone: "",
    });
  };

  handleChange = (field) => (e) => this.setState({ [field]: e.target.value });

  handleSell = () => {
    const {
      selectedCustomer,
      soldToName,
      soldToEmail,
      soldToPhone,
      soldToNotes,
      pricePaid,
      paymentMethod,
      saleLocation,
    } = this.state;
    const { ticket } = this.props;

    const dto = {
      idTicket: ticket.idTicket,
      idCustomer: selectedCustomer ? selectedCustomer.idCustomer : null,
      soldToName: soldToName.trim() || null,
      soldToEmail: soldToEmail.trim() || null,
      soldToPhone: soldToPhone.trim() || null,
      soldToNotes: soldToNotes.trim() || null,
      pricePaid: pricePaid === "" ? null : Number(pricePaid),
      paymentMethod: paymentMethod || null,
      saleLocation: saleLocation.trim() || null,
    };

    this.setState({ saving: true });
    doFetchJson("TEAN", "sellTicket", {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(dto),
    })
      .then((json) => {
        if (json && json.STATE === "SUCCESS") {
          toast.show({ message: "Entrada vendida", type: "success" });
          if (this.props.onSold) this.props.onSold(json.DATA);
        } else {
          toast.show({
            message: (json && json.MESSAGE) || "No se pudo registrar la venta",
            type: "danger",
          });
        }
      })
      .catch(() => toast.show({ message: "Error de red", type: "danger" }))
      .finally(() => this.setState({ saving: false }));
  };

  render() {
    const { show, ticket } = this.props;
    if (!ticket) return null;
    const {
      customerQuery,
      customers,
      searching,
      selectedCustomer,
      soldToName,
      soldToEmail,
      soldToPhone,
      soldToNotes,
      pricePaid,
      paymentMethod,
      saleLocation,
      saving,
    } = this.state;

    return (
      <MDBModal show={show} onHide={this.handleClose} tabIndex="-1">
        <MDBModalDialog centered size="lg">
          <MDBModalContent>
            <MDBModalHeader>
              <MDBModalTitle>
                <MDBIcon fas icon="cash-register" className="me-2" />
                Vender entrada
                {ticket.ticketNumber && (
                  <MDBBadge color="info" className="ms-2">
                    Nº {ticket.ticketNumber}
                  </MDBBadge>
                )}
              </MDBModalTitle>
              <MDBBtn className="btn-close" color="none" onClick={this.handleClose} />
            </MDBModalHeader>
            <MDBModalBody>
              <div className="mb-3">
                <label className="form-label small text-muted">
                  Buscar cliente (nombre, email, teléfono o identificación)
                </label>
                <div className="position-relative">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Empieza a teclear para buscar..."
                    value={customerQuery}
                    onChange={this.handleQueryChange}
                  />
                  {selectedCustomer && (
                    <MDBBtn
                      size="sm"
                      color="link"
                      onClick={this.clearCustomer}
                      style={{ position: "absolute", right: 4, top: 4 }}
                    >
                      <MDBIcon fas icon="times" />
                    </MDBBtn>
                  )}
                </div>
                {searching && (
                  <div className="small text-muted mt-1">
                    <MDBSpinner size="sm" /> Buscando...
                  </div>
                )}
                {customers.length > 0 && !selectedCustomer && (
                  <MDBListGroup small className="mt-1">
                    {customers.map((c) => (
                      <MDBListGroupItem
                        key={c.idCustomer}
                        action
                        onClick={() => this.selectCustomer(c)}
                        style={{ cursor: "pointer" }}
                      >
                        <strong>{c.name}</strong>
                        {c.email && <span className="text-muted ms-2 small">{c.email}</span>}
                        {c.phone && <span className="text-muted ms-2 small">{c.phone}</span>}
                      </MDBListGroupItem>
                    ))}
                  </MDBListGroup>
                )}
                {selectedCustomer && (
                  <div className="alert alert-success py-2 mt-2 small mb-0">
                    <MDBIcon fas icon="user-check" className="me-1" />
                    Cliente seleccionado: <strong>{selectedCustomer.name}</strong>
                  </div>
                )}
              </div>

              <hr />

              <div className="small text-muted mb-2">
                Datos del comprador (puedes completarlos manualmente si no usas un cliente registrado):
              </div>

              <MDBRow className="g-3">
                <MDBCol size="12" md="6">
                  <MDBInput
                    label="Nombre"
                    value={soldToName}
                    onChange={this.handleChange("soldToName")}
                  />
                </MDBCol>
                <MDBCol size="12" md="6">
                  <MDBInput
                    label="Email"
                    type="email"
                    value={soldToEmail}
                    onChange={this.handleChange("soldToEmail")}
                  />
                </MDBCol>
                <MDBCol size="12" md="6">
                  <MDBInput
                    label="Teléfono"
                    value={soldToPhone}
                    onChange={this.handleChange("soldToPhone")}
                  />
                </MDBCol>
                <MDBCol size="12" md="6">
                  <MDBInput
                    label="Lugar de venta"
                    value={saleLocation}
                    onChange={this.handleChange("saleLocation")}
                  />
                </MDBCol>
                <MDBCol size="12" md="6">
                  <MDBInput
                    label="Precio cobrado (€)"
                    type="number"
                    step="0.01"
                    min="0"
                    value={pricePaid}
                    onChange={this.handleChange("pricePaid")}
                  />
                </MDBCol>
                <MDBCol size="12" md="6">
                  <label className="form-label small text-muted">Método de pago</label>
                  <select
                    className="form-select"
                    value={paymentMethod}
                    onChange={this.handleChange("paymentMethod")}
                  >
                    <option value="CASH">Efectivo</option>
                    <option value="CARD">Tarjeta</option>
                    <option value="TRANSFER">Transferencia</option>
                    <option value="BIZUM">Bizum</option>
                    <option value="OTHER">Otro</option>
                  </select>
                </MDBCol>
                <MDBCol size="12">
                  <label className="form-label small text-muted">Notas</label>
                  <textarea
                    className="form-control"
                    rows="2"
                    value={soldToNotes}
                    onChange={this.handleChange("soldToNotes")}
                  />
                </MDBCol>
              </MDBRow>
            </MDBModalBody>
            <MDBModalFooter>
              <MDBBtn color="secondary" outline onClick={this.handleClose} disabled={saving}>
                Cancelar
              </MDBBtn>
              <MDBBtn color="success" onClick={this.handleSell} disabled={saving}>
                {saving ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" />
                    Vendiendo...
                  </>
                ) : (
                  <>
                    <MDBIcon fas icon="check" className="me-1" />
                    Confirmar venta
                  </>
                )}
              </MDBBtn>
            </MDBModalFooter>
          </MDBModalContent>
        </MDBModalDialog>
      </MDBModal>
    );
  }
}

export default SellTicketModal;
